// @ts-strict-ignore
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { uniqueId } from 'lodash-es';
import { Editor } from 'primeng/editor';
import Quill, { QuillOptions } from 'quill';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { toHtml } from '../../../../functions/to-html';
import { LanguageService } from '../../../../services/language.service';
import { TranslationService } from '../../../../services/translation-service';
import { UserFacadeService } from '../../../../store/user/user-facade.service';
import { InputBaseComponent } from '../input-base/input-base.component';
import { DEFAULT_FORMATS, TAB_NAVIGATION_HANDLER } from './input-editor.constants';

/**
 * @deprecated Use app-input-editor instead
 */
@Component({
  selector: 'ui-input-editor',
  templateUrl: './input-editor.component.html',
  styleUrls: ['./input-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InputEditorComponent extends InputBaseComponent<string> implements OnChanges, OnDestroy {
  @Input() enableVerticalResize: boolean = false;
  @Input() formats: QuillOptions['formats'] = DEFAULT_FORMATS;
  @Input() set disabled(disabled: boolean) {
    this.isDisabled = disabled;
    // this.control?.control?.disable();
  }
  @Input() modules: QuillOptions['modules'] = TAB_NAVIGATION_HANDLER;
  @Input() name: string = uniqueId('input-text-editor');
  @Input() readonly: boolean = false;
  @Input() style: { [klass: string]: any } = { width: '100%', height: '100%' };
  @Output() blur: EventEmitter<void> = new EventEmitter();
  @Output() htmlChange: EventEmitter<string> = new EventEmitter();
  @Output() onFocus: EventEmitter<void> = new EventEmitter();
  @Output() textChange: EventEmitter<string> = new EventEmitter();
  @ViewChild('pEditor') pEditorElement: Editor;
  @ViewChild('wrapperElement') wrapperElement: ElementRef<HTMLDivElement>;

  private _inputElement: HTMLElement;
  private _linkActionElement: HTMLElement;
  private _linkSaved: boolean;

  private readonly _LINK_TOOLTIP_STRINGS: [string, string][] = [
    ['--link-tooltip-enter-url', `'${this._translate(UI_STRINGS.INPUT_COMPONENTS.ENTER_URL)}:'`],
    ['--link-tooltip-visit-url', `'${this._translate(UI_STRINGS.INPUT_COMPONENTS.VISIT_URL)}:'`],
    ['--link-tooltip-save', `'${this._translate(UI_STRINGS.GLOBAL.SAVE)}'`],
    ['--link-tooltip-edit', `'${this._translate(UI_STRINGS.INPUT_COMPONENTS.EDIT)}'`],
    ['--link-tooltip-remove', `'${this._translate(UI_STRINGS.GLOBAL.REMOVE)}'`],
  ];

  constructor(
    @Self() @Optional() public control: NgControl,
    public userFacadeService: UserFacadeService,
    private _languageService: LanguageService,
    private _renderer: Renderer2,
    private _translationService: TranslationService
  ) {
    super(control);
  }

  ngOnChanges({ formControl, formControlName }: SimpleChanges): void {
    if (formControl || formControlName) {
      this.value = toHtml(this.value);
    }
  }

  ngOnDestroy() {
    if (!this._inputElement || !this._linkActionElement) {
      return;
    }
    this._inputElement.removeEventListener('blur', this._onBlur.bind(this));
    this._inputElement.removeEventListener('focus', this._onBlur.bind(this));
    this._linkActionElement.removeEventListener('mousedown', this._onLinkAction.bind(this));
  }

  onEditorInit({ editor }: { editor: Editor['quill'] }) {
    this._extendLinkSanitizer();
    this._inputElement = editor.container.firstChild;
    this._inputElement.classList.add('rich-text-format');
    this._inputElement.addEventListener('blur', this._onBlur.bind(this));
    this._inputElement.addEventListener('focus', this._onFocus.bind(this));
    this._LINK_TOOLTIP_STRINGS.forEach((s) => {
      this.wrapperElement.nativeElement.style.setProperty(s[0], s[1]);
    });
    const linkInput = editor.container.querySelector('input[data-link]');
    linkInput.placeholder = linkInput.dataset.link = 'https://climateview.global';
    this._linkActionElement = editor.container.querySelector('.ql-action');
    this._linkActionElement.addEventListener('mousedown', this._onLinkAction.bind(this));
    if (this.enableVerticalResize) {
      this._setNewStylesOnElementsForVerticalResize();
    }
  }

  onTextChange({ htmlValue, textValue }: { htmlValue: string; textValue: string }) {
    if (this._linkSaved) {
      this._inputElement.focus();
      this._linkSaved = false;
    }
    this.textChange.emit(textValue);
    this.htmlChange.emit(toHtml(htmlValue));
  }

  private _emit() {
    this.value = toHtml(this.value);
    this.onChange(this.value);
    this.onTouched();
  }

  private _extendLinkSanitizer() {
    const linkFormat = Quill.import('formats/link') as any;
    const quillLinkSanitizer = linkFormat.sanitize;
    linkFormat.sanitize = function customSanitizeLinkInput(link: string) {
      let sanitizedLink = link;
      if (/^http:\/\//.test(sanitizedLink)) {
        sanitizedLink = sanitizedLink.replace('http://', 'https://');
      }
      if (!/^https:\/\//.test(sanitizedLink)) {
        sanitizedLink = 'https://' + sanitizedLink;
      }
      return quillLinkSanitizer.call(this, sanitizedLink);
    };
  }

  private _onBlur({ relatedTarget }: FocusEvent) {
    if (!this.pEditorElement.el.nativeElement.contains(relatedTarget)) {
      this.blur.emit();
      if (this.control.value !== this.value) {
        this._emit();
      }
    }
  }
  private _onFocus(_: FocusEvent) {
    this.onFocus.emit();
  }

  private _onLinkAction(event: MouseEvent) {
    //Makes sure focus is returned to the editor when a link is saved
    const linkAction = getComputedStyle(<HTMLAnchorElement>event.target, ':after').content;
    this._linkSaved = linkAction === `"${this._translate(UI_STRINGS.GLOBAL.SAVE)}"`;
  }

  private _setNewStylesOnElementsForVerticalResize() {
    const wrapperElement = this.wrapperElement.nativeElement;
    this._renderer.setStyle(wrapperElement, 'height', '100%');
    this._renderer.setStyle(wrapperElement, `--height-for-vertical-resize`, this.style.height, 2);
  }

  private _translate(stringId: string) {
    return this._translationService.translate(this._languageService.language, stringId);
  }
}
